import {
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  type Query,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import type { Business, Project, ReferralSourceReport, SalesRepReport, AgingBucket } from '@/types';
import { projectsCollection } from '@/config/collections';

// ===========================================
// Types
// ===========================================

export type ReportMode = 'closed' | 'pipeline';

export interface ReportFilters {
  business: Business;
  startDate: Date;
  endDate: Date;
  salesRepId?: string;
  referralSourceId?: string;
  commissionStatus?: 'all' | 'paid' | 'unpaid';
  /** Logged-in user id — used to scope the query for non-admin reps. */
  currentUserId?: string;
  /** When false, the query is constrained to the current user's own projects. */
  isAdmin?: boolean;
  /** 'closed' = jobs with a closeDate in range; 'pipeline' = in-progress jobs. */
  mode?: ReportMode;
}

export interface ReportSummary {
  totalProjects: number;
  totalRevenue: number;
  totalCommission: number;
  paidCommission: number;
  unpaidCommission: number;
  // Partner (referral-source) kickbacks
  totalPartnerCommission: number;
  paidPartnerCommission: number;
  unpaidPartnerCommission: number;
}

export interface ReportData {
  projects: Project[];
  summary: ReportSummary;
  filters: ReportFilters;
  mode: ReportMode;
  generatedAt: Date;
}

// ===========================================
// Collection Reference
// ===========================================

const projectsCollectionRef = projectsCollection(db);

// ===========================================
// Helpers
// ===========================================

/** Actual paid dollars for a rep commission, tolerant of the legacy field. */
function repPaidAmount(p: Project): number {
  return (
    (p as { commissionPaidAmount?: number }).commissionPaidAmount ??
    p.paidAmount ??
    p.expectedCommission ??
    0
  );
}

/** Actual paid dollars for a partner commission, tolerant of the legacy field. */
function partnerPaidAmount(p: Project): number {
  return (
    (p as { partnerCommissionPaidAmount?: number }).partnerCommissionPaidAmount ??
    p.partnerPaidAmount ??
    p.partnerExpectedCommission ??
    0
  );
}

function mapDoc(doc: {
  id: string;
  data: () => Record<string, unknown>;
}): Project {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    closeDate: (data.closeDate as Timestamp | undefined)?.toDate?.() || null,
    commissionPaidDate: (data.commissionPaidDate as Timestamp | undefined)?.toDate?.() || null,
    partnerCommissionPaidDate:
      (data.partnerCommissionPaidDate as Timestamp | undefined)?.toDate?.() || null,
    createdAt: (data.createdAt as Timestamp | undefined)?.toDate?.() || new Date(),
    updatedAt: (data.updatedAt as Timestamp | undefined)?.toDate?.() || new Date(),
  } as unknown as Project;
}

// ===========================================
// Report Generation
// ===========================================

/**
 * Generate a commission report.
 *
 * - mode 'closed' (default): jobs whose closeDate falls in [startDate, endDate].
 * - mode 'pipeline': in-progress jobs (no closeDate) — "potential" commission.
 *
 * For non-admin reps the query is constrained to salesRepUserId == currentUserId
 * so it satisfies the Firestore security rules (an unconstrained query is
 * rejected for reps and would silently return nothing).
 */
export async function generateReport(filters: ReportFilters): Promise<ReportData> {
  const {
    business,
    startDate,
    endDate,
    salesRepId,
    referralSourceId,
    commissionStatus,
    currentUserId,
    isAdmin,
    mode = 'closed',
  } = filters;

  const repScope = !isAdmin && currentUserId ? currentUserId : undefined;

  let q: Query;
  if (mode === 'pipeline') {
    // In-progress: scope tightly, then filter to null closeDate in memory
    // (Firestore can't cheaply query "field is null" alongside an orderBy).
    if (repScope) {
      q = query(projectsCollectionRef, where('salesRepUserId', '==', repScope));
    } else {
      q = query(projectsCollectionRef, where('business', '==', business));
    }
  } else if (repScope) {
    // Closed jobs for a specific rep — must include salesRepUserId to pass rules.
    // business is included so this reuses the (business, salesRepUserId,
    // closeDate) composite index that already exists.
    q = query(
      projectsCollectionRef,
      where('business', '==', business),
      where('salesRepUserId', '==', repScope),
      where('closeDate', '>=', Timestamp.fromDate(startDate)),
      where('closeDate', '<=', Timestamp.fromDate(endDate)),
      orderBy('closeDate', 'desc')
    );
  } else {
    // Closed jobs for the whole business (admin/owner).
    q = query(
      projectsCollectionRef,
      where('business', '==', business),
      where('closeDate', '>=', Timestamp.fromDate(startDate)),
      where('closeDate', '<=', Timestamp.fromDate(endDate)),
      orderBy('closeDate', 'desc')
    );
  }

  const snapshot = await getDocs(q);
  let projects: Project[] = snapshot.docs.map(mapDoc);

  // In-memory filters (kept out of the query to avoid extra composite indexes).
  if (mode === 'pipeline') {
    projects = projects.filter((p) => !p.closeDate);
  }
  // When scoped by rep we filtered on salesRepUserId, so also constrain business.
  if (repScope) {
    projects = projects.filter((p) => p.business === business);
  }

  if (salesRepId) {
    projects = projects.filter((p) => p.salesRepUserId === salesRepId);
  }
  if (referralSourceId) {
    projects = projects.filter((p) => p.referralSourceId === referralSourceId);
  }
  if (commissionStatus === 'paid') {
    projects = projects.filter((p) => p.commissionPaid);
  } else if (commissionStatus === 'unpaid') {
    projects = projects.filter((p) => !p.commissionPaid);
  }

  const summary = summarize(projects);

  return { projects, summary, filters, mode, generatedAt: new Date() };
}

function summarize(projects: Project[]): ReportSummary {
  return {
    totalProjects: projects.length,
    totalRevenue: projects.reduce((s, p) => s + (p.totalRevenue || 0), 0),
    totalCommission: projects.reduce((s, p) => s + (p.expectedCommission || 0), 0),
    paidCommission: projects.filter((p) => p.commissionPaid).reduce((s, p) => s + repPaidAmount(p), 0),
    unpaidCommission: projects
      .filter((p) => !p.commissionPaid)
      .reduce((s, p) => s + (p.expectedCommission || 0), 0),
    totalPartnerCommission: projects.reduce((s, p) => s + (p.partnerExpectedCommission || 0), 0),
    paidPartnerCommission: projects
      .filter((p) => p.partnerCommissionPaid)
      .reduce((s, p) => s + partnerPaidAmount(p), 0),
    unpaidPartnerCommission: projects
      .filter((p) => !p.partnerCommissionPaid)
      .reduce((s, p) => s + (p.partnerExpectedCommission || 0), 0),
  };
}

// ===========================================
// Grouped Report Builders (By Referral Source / By Sales Rep / Aging)
// ===========================================

export function buildReferralSourceReport(projects: Project[]): ReferralSourceReport[] {
  const map = new Map<string, ReferralSourceReport>();
  for (const p of projects) {
    const id = p.referralSourceId || '__direct__';
    const name = p.referralSourceName || 'Direct (no referral)';
    const row =
      map.get(id) ||
      { companyId: id, companyName: name, jobCount: 0, totalRevenue: 0, expectedCommission: 0, paidCommission: 0, unpaidCommission: 0 };
    row.jobCount += 1;
    row.totalRevenue += p.totalRevenue || 0;
    row.expectedCommission += p.partnerExpectedCommission || 0;
    if (p.partnerCommissionPaid) row.paidCommission += partnerPaidAmount(p);
    else row.unpaidCommission += p.partnerExpectedCommission || 0;
    map.set(id, row);
  }
  return Array.from(map.values()).sort((a, b) => b.expectedCommission - a.expectedCommission);
}

export function buildSalesRepReport(projects: Project[]): SalesRepReport[] {
  const map = new Map<string, SalesRepReport>();
  for (const p of projects) {
    const id = p.salesRepUserId || '__unassigned__';
    const name = p.salesRepName || 'Unassigned';
    const row =
      map.get(id) ||
      { userId: id, displayName: name, jobCount: 0, totalRevenue: 0, expectedCommission: 0, paidCommission: 0, unpaidCommission: 0 };
    row.jobCount += 1;
    row.totalRevenue += p.totalRevenue || 0;
    row.expectedCommission += p.expectedCommission || 0;
    if (p.commissionPaid) row.paidCommission += repPaidAmount(p);
    else row.unpaidCommission += p.expectedCommission || 0;
    map.set(id, row);
  }
  return Array.from(map.values()).sort((a, b) => b.expectedCommission - a.expectedCommission);
}

/**
 * Aging report of UNPAID rep commissions bucketed by days since closeDate.
 * Buckets default to 0-30 / 31-60 / 61-90 / 90+.
 */
export function buildAgingReport(
  projects: Project[],
  buckets: number[] = [30, 60, 90]
): AgingBucket[] {
  const now = Date.now();
  const rows: AgingBucket[] = [];
  const bounds = [0, ...buckets];
  for (let i = 0; i < bounds.length; i++) {
    const minDays = bounds[i];
    const maxDays = i < buckets.length ? buckets[i] : undefined;
    rows.push({
      label: maxDays ? `${minDays}-${maxDays} days` : `${minDays}+ days`,
      minDays,
      maxDays,
      count: 0,
      totalAmount: 0,
    });
  }
  for (const p of projects) {
    if (p.commissionPaid) continue;
    if (!p.expectedCommission) continue;
    if (!p.closeDate) continue;
    const days = Math.floor((now - new Date(p.closeDate).getTime()) / 86_400_000);
    const bucket =
      rows.find((r) => days >= r.minDays && (r.maxDays === undefined || days <= r.maxDays)) ||
      rows[rows.length - 1];
    bucket.count += 1;
    bucket.totalAmount += p.expectedCommission || 0;
  }
  return rows;
}

// ===========================================
// CSV Export
// ===========================================

export function reportToCSV(report: ReportData): string {
  const headers = [
    'Project Name',
    'Close Date',
    'Business',
    'Project Type',
    'Contract Amount',
    'Change Orders',
    'Total Revenue',
    'Rep Commission',
    'Rep Status',
    'Rep Paid Amount',
    'Rep Paid Date',
    'Sales Rep',
    'Referral Source',
    'Partner Commission',
    'Partner Status',
    'Partner Paid Amount',
  ];

  const rows = report.projects.map((project) => [
    escapeCSV(project.name),
    project.closeDate ? new Date(project.closeDate).toLocaleDateString() : '',
    project.business,
    project.projectType || '',
    formatNumber(project.contractAmount),
    formatNumber(project.changeOrderTotal),
    formatNumber(project.totalRevenue),
    formatNumber(project.expectedCommission),
    project.commissionPaid ? 'Paid' : 'Unpaid',
    formatNumber(project.commissionPaid ? repPaidAmount(project) : 0),
    project.commissionPaidDate ? new Date(project.commissionPaidDate).toLocaleDateString() : '',
    escapeCSV(project.salesRepName || ''),
    escapeCSV(project.referralSourceName || ''),
    formatNumber(project.partnerExpectedCommission),
    project.partnerCommissionPaid ? 'Paid' : 'Unpaid',
    formatNumber(project.partnerCommissionPaid ? partnerPaidAmount(project) : 0),
  ]);

  const s = report.summary;
  rows.push([]);
  rows.push(['SUMMARY']);
  rows.push(['Total Projects', s.totalProjects.toString()]);
  rows.push(['Total Revenue', formatNumber(s.totalRevenue)]);
  rows.push(['Total Rep Commission', formatNumber(s.totalCommission)]);
  rows.push(['Paid Rep Commission', formatNumber(s.paidCommission)]);
  rows.push(['Unpaid Rep Commission', formatNumber(s.unpaidCommission)]);
  rows.push(['Total Partner Commission', formatNumber(s.totalPartnerCommission)]);
  rows.push(['Paid Partner Commission', formatNumber(s.paidPartnerCommission)]);
  rows.push(['Unpaid Partner Commission', formatNumber(s.unpaidPartnerCommission)]);
  rows.push([]);
  rows.push(['Report Mode', report.mode]);
  rows.push(['Report Generated', report.generatedAt.toLocaleString()]);
  rows.push([
    'Date Range',
    `${report.filters.startDate.toLocaleDateString()} - ${report.filters.endDate.toLocaleDateString()}`,
  ]);

  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
}

export function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

// ===========================================
// Helpers
// ===========================================

function escapeCSV(value: string): string {
  if (!value) return '';
  if (value.includes(',') || value.includes('\n') || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0';
  return value.toFixed(2);
}

// ===========================================
// Date Helpers
// ===========================================

export function getCurrentQuarterRange(): { start: Date; end: Date } {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const startMonth = quarter * 3;
  const start = new Date(now.getFullYear(), startMonth, 1);
  const end = new Date(now.getFullYear(), startMonth + 3, 0, 23, 59, 59);
  return { start, end };
}

export function getPreviousQuarterRange(): { start: Date; end: Date } {
  const now = new Date();
  let year = now.getFullYear();
  let quarter = Math.floor(now.getMonth() / 3) - 1;
  if (quarter < 0) {
    quarter = 3;
    year -= 1;
  }
  const startMonth = quarter * 3;
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0, 23, 59, 59);
  return { start, end };
}

export function getYTDRange(): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return { start, end };
}

export function getQuarterLabel(date: Date): string {
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return `Q${quarter} ${date.getFullYear()}`;
}
